import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaEye, FaSearch, FaDownload, FaSpinner, FaFilter, FaEdit, FaTrash, FaPlus, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fraudService } from '../../../../services/fraudService';
import FraudGateEventsPanel from './FraudGateEventsPanel';

const FraudDetection = () => {
  const [activeTab, setActiveTab] = useState('gates');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    critical: 0,
    high: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    severity: 'all',
    type: 'all',
    search: ''
  });

  useEffect(() => {
    loadAlerts();
    loadStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadAlerts();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [filters]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await fraudService.getAllAlerts({
        status: filters.status !== 'all' ? filters.status : undefined,
        severity: filters.severity !== 'all' ? filters.severity : undefined,
        type: filters.type !== 'all' ? filters.type : undefined
      });
      
      // Filter by search term if provided
      let filtered = data;
      if (filters.search) {
        filtered = data.filter(alert => 
          alert.alertId?.toLowerCase().includes(filters.search.toLowerCase()) ||
          alert.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
          alert.userId?.name?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setAlerts(filtered);
    } catch (error) {
      toast.error(error.message || 'Failed to load fraud alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await fraudService.getAlertStats();
      setStats({
        total: data.total || 0,
        pending: data.pending || 0,
        critical: data.critical || 0,
        high: data.high || 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleViewAlert = async (alert) => {
    try {
      setLoading(true);
      const fullAlert = await fraudService.getAlertById(alert._id || alert.id);
      setSelectedAlert(fullAlert);
      setShowAlertModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load alert details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (alertId, status, resolution = null, actionTaken = null) => {
    try {
      setLoading(true);
      await fraudService.updateAlertStatus(alertId, { status, resolution, actionTaken });
      toast.success('Alert status updated successfully');
      await loadAlerts();
      await loadStats();
      setShowAlertModal(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update alert status');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const csvData = await fraudService.exportAlerts({
        status: filters.status !== 'all' ? filters.status : undefined,
        severity: filters.severity !== 'all' ? filters.severity : undefined,
        type: filters.type !== 'all' ? filters.type : undefined
      });
      
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `fraud_alerts_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Alerts exported successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to export alerts');
    } finally {
      setExporting(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Under Review': return 'bg-blue-100 text-blue-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Confirmed': return 'bg-red-100 text-red-800';
      case 'False Positive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'suspicious_order': 'Suspicious Order',
      'payment_anomaly': 'Payment Anomaly',
      'account_abuse': 'Account Abuse',
      'velocity_check': 'Velocity Check',
      'address_mismatch': 'Address Mismatch',
      'device_fingerprint': 'Device Fingerprint',
      'multiple_accounts': 'Multiple Accounts',
      'unusual_activity': 'Unusual Activity',
      'refund_abuse': 'Refund Abuse',
      'promo_code_abuse': 'Promo Code Abuse'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Fraud Detection & Monitoring</h2>
          <p className="text-xs text-gray-600 mt-1">
            {activeTab === 'gates' ? '6-gate seller fraud system (Sparepe)' : 'Legacy payment & order fraud alerts'}
          </p>
        </div>
        <div className="flex rounded-lg border overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setActiveTab('gates')}
            className={`px-4 py-2 font-semibold ${activeTab === 'gates' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            6-Gate Events
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('legacy')}
            className={`px-4 py-2 font-semibold ${activeTab === 'legacy' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            Legacy Alerts
          </button>
        </div>
      </div>

      {activeTab === 'gates' ? (
        <FraudGateEventsPanel />
      ) : (
        <>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-end gap-4">
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={exporting || alerts.length === 0}
            className="bg-white border border-red-500 text-red-500 rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors"
            title="Export alerts to CSV"
          >
            {exporting ? <FaSpinner className="animate-spin text-red-500" /> : <FaDownload className="text-red-500" />} 
            <span className="text-red-500">Export</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-purple-700 mb-1">Total Alerts</p>
              <p className="text-2xl font-bold text-purple-900">{stats.total.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <FaShieldAlt className="text-xl text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-yellow-700 mb-1">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-900">{stats.pending.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <FaClock className="text-xl text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-6 border border-red-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-red-700 mb-1">Critical</p>
              <p className="text-2xl font-bold text-red-900">{stats.critical.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <FaExclamationTriangle className="text-xl text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg p-6 border border-orange-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-orange-700 mb-1">High Priority</p>
              <p className="text-2xl font-bold text-orange-900">{stats.high.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <FaExclamationTriangle className="text-xl text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search alerts..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Under Review">Under Review</option>
            <option value="Resolved">Resolved</option>
            <option value="Confirmed">Confirmed</option>
            <option value="False Positive">False Positive</option>
          </select>
          <select
            value={filters.severity}
            onChange={(e) => setFilters({...filters, severity: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Severity</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          <select
            value={filters.type}
            onChange={(e) => setFilters({...filters, type: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="suspicious_order">Suspicious Order</option>
            <option value="payment_anomaly">Payment Anomaly</option>
            <option value="account_abuse">Account Abuse</option>
            <option value="velocity_check">Velocity Check</option>
            <option value="multiple_accounts">Multiple Accounts</option>
            <option value="promo_code_abuse">Promo Code Abuse</option>
          </select>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Loading alerts...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alert ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {alerts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No fraud alerts found.
                    </td>
                  </tr>
                ) : (
                  alerts.map((alert) => (
                    <tr key={alert._id || alert.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{alert.alertId}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{getTypeLabel(alert.type)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                alert.riskScore >= 70 ? 'bg-red-600' :
                                alert.riskScore >= 40 ? 'bg-orange-600' :
                                'bg-yellow-600'
                              }`}
                              style={{ width: `${alert.riskScore}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{alert.riskScore}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {alert.userId?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(alert.status)}`}>
                          {alert.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {alert.createdAt ? new Date(alert.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={() => handleViewAlert(alert)}
                          className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition" 
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alert Detail Modal */}
      {showAlertModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedAlert.alertId}</h3>
                <p className="text-sm text-gray-600 mt-1">{getTypeLabel(selectedAlert.type)}</p>
              </div>
              <button
                onClick={() => {
                  setShowAlertModal(false);
                  setSelectedAlert(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Alert Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Severity</p>
                  <span className={`px-3 py-1 text-sm rounded-full border ${getSeverityColor(selectedAlert.severity)}`}>
                    {selectedAlert.severity}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Risk Score</p>
                  <p className="text-lg font-bold text-gray-900">{selectedAlert.riskScore}/100</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <select
                    value={selectedAlert.status}
                    onChange={(e) => handleUpdateStatus(selectedAlert._id || selectedAlert.id, e.target.value)}
                    className="mt-1 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="False Positive">False Positive</option>
                  </select>
                </div>
                <div>
                  <p className="text-sm text-gray-600">User</p>
                  <p className="font-semibold">{selectedAlert.userId?.name || 'N/A'}</p>
                  <p className="text-xs text-gray-500">{selectedAlert.userId?.email || ''}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Description</p>
                <p className="text-sm text-gray-600">{selectedAlert.description}</p>
              </div>

              {/* Indicators */}
              {selectedAlert.indicators && selectedAlert.indicators.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Fraud Indicators</p>
                  <div className="space-y-2">
                    {selectedAlert.indicators.map((indicator, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <p className="text-xs font-medium text-gray-900">{indicator.rule}</p>
                        <p className="text-xs text-gray-600">
                          Value: {JSON.stringify(indicator.value)} | Threshold: {JSON.stringify(indicator.threshold)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              {selectedAlert.metadata && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Metadata</p>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                      {JSON.stringify(selectedAlert.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleUpdateStatus(selectedAlert._id || selectedAlert.id, 'Resolved', 'Resolved by admin', 'None')}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Mark as Resolved
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedAlert._id || selectedAlert.id, 'False Positive', 'False positive alert', 'None')}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Mark as False Positive
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedAlert._id || selectedAlert.id, 'Confirmed', 'Confirmed fraud', 'Account Flagged')}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Confirm Fraud
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default FraudDetection;

